import { useState, useCallback, useEffect } from 'react';
import {
	FormField,
	FormInput,
	FormSectionTitle,
	DatePicker,
	DetailModal,
	BrainIcon,
} from '@practium/ui';
import CalendarToday from '@mui/icons-material/CalendarToday';
import Info from '@mui/icons-material/Info';
import { RichTextarea } from '../../../../../features/recommendations';
import { FormModal } from '../shared/FormModal';
import type { FormModalMode } from '../shared/FormModal';
import { useNoteForm } from '../../../hooks/useNoteForm';
import type { FieldDiaryEntry, NoteFormData } from '../../../types';
import { formatDiaryDate } from '../../../utils/entry.utils';
import formStyles from './NoteForm.module.css';
import styles from './NoteModal.module.css';
import './NoteModal.css';

export interface NoteModalProps {
	isOpen: boolean;
	monthLabel: string;
	initialMode?: FormModalMode;
	viewEntry?: FieldDiaryEntry;
	onClose: () => void;
	onSave: (form: NoteFormData, entryId?: string) => Promise<FieldDiaryEntry>;
	onDelete?: (entry: FieldDiaryEntry) => void;
}

export const NoteModal = ({
	isOpen,
	monthLabel,
	initialMode = 'add',
	viewEntry,
	onClose,
	onSave,
	onDelete,
}: NoteModalProps) => {
	const [currentMode, setCurrentMode] = useState<FormModalMode>(initialMode);
	const [currentEntry, setCurrentEntry] = useState<FieldDiaryEntry | undefined>(viewEntry);

	useEffect(() => {
		if (isOpen) {
			setCurrentMode(initialMode);
			setCurrentEntry(viewEntry);
		}
	}, [isOpen, initialMode, viewEntry]);

	const { form, errors, isSaving, set, populate, reset, handleSubmit } = useNoteForm({
		onSubmit: (data) => onSave(data, currentEntry?._id),
		onSuccess: (saved) => {
			setCurrentEntry(saved);
			setCurrentMode('view');
		},
	});

	const handleClose = () => {
		reset();
		setCurrentMode(initialMode);
		setCurrentEntry(viewEntry);
		onClose();
	};

	const handleEdit = useCallback(() => {
		if (currentEntry?.noteData) populate(currentEntry.noteData);
		setCurrentMode('edit');
	}, [currentEntry, populate]);

	const handleDelete = onDelete && currentEntry ? () => onDelete(currentEntry) : undefined;

	const handleCancelEdit = useCallback(() => {
		reset();
		setCurrentMode('view');
	}, [reset]);

	const data = currentEntry?.noteData;
	const iconColor = 'var(--primary)';

	if (currentMode === 'view' && isOpen) {
		return (
			<DetailModal
				title="Nota Livre"
				metaItems={[
					{
						icon: <CalendarToday className={styles.metaIcon} style={{ color: iconColor }} />,
						text: data?.date ? formatDiaryDate(data.date) : '—',
					},
					...(data?.category
						? [
								{
									icon: <Info className={styles.metaIcon} style={{ color: iconColor }} />,
									text: data.category,
								},
							]
						: []),
					...(data?.createdBy
						? [
								{
									icon: <BrainIcon color={iconColor} />,
									text: `Criado por ${data.createdBy}`,
								},
							]
						: []),
				]}
				sections={[
					{
						title: '',
						fields: [
							{
								label: '',
								span: 'full',
								showLabel: false,
								value: (
									<div className="note-html-body">
										<h2>{data?.title ?? ''}</h2>
										<div dangerouslySetInnerHTML={{ __html: data?.note ?? '' }} />
									</div>
								),
							},
						],
					},
				]}
				footer={
					<div className={styles.viewFooter}>
						{handleDelete && (
							<button type="button" className={styles.deleteBtn} onClick={handleDelete}>
								Excluir
							</button>
						)}
						<button type="button" className={styles.editBtn} onClick={handleEdit}>
							Editar
						</button>
					</div>
				}
				onClose={handleClose}
				dismissible={false}
			/>
		);
	}

	const titleMap: Record<'add' | 'edit', string> = {
		add: `Nota Livre — ${monthLabel}`,
		edit: `Editar Nota — ${monthLabel}`,
	};

	return (
		<FormModal
			isOpen={isOpen}
			title={titleMap[currentMode as 'add' | 'edit']}
			onClose={handleClose}
			mode={currentMode}
			onSave={handleSubmit}
			onCancel={currentMode === 'edit' ? handleCancelEdit : undefined}
			isSaving={isSaving}
			saveLabel="Salvar Nota"
			onDelete={currentMode === 'edit' ? handleDelete : undefined}
		>
			<div className={formStyles.form}>
				<div className={formStyles.section}>
					<FormSectionTitle>Conteúdo</FormSectionTitle>
					<div className={formStyles.fields}>
						<FormField label="Título" required error={errors.title}>
							<FormInput
								placeholder="Insira o título da nota"
								value={form.title}
								onChange={(e) => set('title', e.target.value)}
								hasError={!!errors.title}
							/>
						</FormField>

						<div className={formStyles.row2}>
							<FormField label="Data" required error={errors.date}>
								<DatePicker
									value={form.date}
									onChange={(v) => set('date', v)}
									hasError={!!errors.date}
								/>
							</FormField>
							<FormField label="Categoria" error={errors.category}>
								<FormInput
									placeholder="Insira uma categoria opcional"
									value={form.category}
									onChange={(e) => set('category', e.target.value)}
									hasError={!!errors.category}
								/>
							</FormField>
						</div>

						<FormField label="Nota" required error={errors.note}>
							<div className={formStyles.noteWrapper}>
								<RichTextarea
									value={form.note}
									onChange={(v) => set('note', v)}
									placeholder="Insira o conteúdo da nota"
									hasError={!!errors.note}
								/>
							</div>
						</FormField>
					</div>
				</div>
			</div>
		</FormModal>
	);
};
